/**
 * Saved-products store (client only).
 *
 * One shared store so every heart button and the account list always show the
 * same state. Persistence rules:
 *  - Guest saves live on this device and are kept until they have been
 *    successfully merged into the signed-in account — a failed merge never
 *    discards them.
 *  - A write that fails is rolled back in the UI, reported to the customer,
 *    and produces NO success event.
 *  - Success events are emitted only after the write actually persisted.
 */
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/customer-intelligence/tracker";
import { dedupe, readGuestWishlist, writeGuestWishlist } from "@/lib/wishlist";

export interface WishlistState {
  handles: string[];
  ready: boolean;
  signedIn: boolean;
  /** Handles with a write in flight — their buttons stay disabled. */
  pending: string[];
  /** Set when the last load or merge failed; the UI keeps what it has. */
  error: string | null;
}

const EMPTY: WishlistState = { handles: [], ready: false, signedIn: false, pending: [], error: null };

let state: WishlistState = EMPTY;
let userId: string | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

function setState(patch: Partial<WishlistState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): WishlistState {
  return state;
}

export function getServerSnapshot(): WishlistState {
  return EMPTY;
}

/** Test seam. */
export function __resetWishlistStore() {
  state = EMPTY;
  userId = null;
  loading = null;
}

async function fetchRows(): Promise<string[] | null> {
  const { data, error } = await supabase
    .from("wishlist_items")
    .select("product_handle, created_at")
    .order("created_at", { ascending: false });
  if (error) return null;
  return dedupe((data ?? []).map((r) => r.product_handle));
}

/**
 * Merge this device's guest saves into the account. Guest storage is cleared
 * only when the merge actually succeeded.
 */
async function mergeGuestItems(uid: string): Promise<boolean> {
  const guest = readGuestWishlist();
  if (guest.length === 0) return true;
  const { error } = await supabase
    .from("wishlist_items")
    .upsert(
      guest.map((h) => ({ user_id: uid, product_handle: h })),
      { onConflict: "user_id,product_handle", ignoreDuplicates: true },
    );
  if (error) return false;
  writeGuestWishlist([]);
  return true;
}

export async function load(): Promise<void> {
  const { data, error: authError } = await supabase.auth.getUser();
  const uid = authError ? null : data.user?.id ?? null;
  userId = uid;

  if (!uid) {
    setState({ handles: readGuestWishlist(), ready: true, signedIn: false, error: null });
    return;
  }

  const merged = await mergeGuestItems(uid);
  const rows = await fetchRows();
  if (rows === null) {
    // Read failed: keep whatever we can still show rather than blanking the
    // list, and say so.
    setState({
      handles: dedupe([...readGuestWishlist(), ...state.handles]),
      ready: true,
      signedIn: true,
      error: "Couldn't load your saved products. Showing the latest we have.",
    });
    return;
  }
  setState({
    handles: dedupe([...(merged ? [] : readGuestWishlist()), ...rows]),
    ready: true,
    signedIn: true,
    error: merged ? null : "Saved items on this device couldn't be added to your account yet.",
  });
}

export function ensureLoaded(): void {
  if (loading) return;
  loading = load().finally(() => {
    loading = null;
  });
}

export function startAuthWatch(): () => void {
  const { data: sub } = supabase.auth.onAuthStateChange((event) => {
    if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
      loading = load().finally(() => {
        loading = null;
      });
    }
  });
  return () => sub.subscription.unsubscribe();
}

export type ToggleResult =
  | { ok: true; saved: boolean }
  | { ok: false; reason: "not_ready" | "busy" | "write_failed" };

/** Adds or removes one product. Only a persisted change updates the UI state. */
export async function toggleSaved(rawHandle: string): Promise<ToggleResult> {
  const handle = rawHandle.trim().toLowerCase();
  if (!state.ready) return { ok: false, reason: "not_ready" };
  if (state.pending.includes(handle)) return { ok: false, reason: "busy" };

  const wasSaved = state.handles.includes(handle);
  const before = state.handles;
  const next = wasSaved ? before.filter((h) => h !== handle) : dedupe([handle, ...before]);

  // Optimistic, but always reverted when the write does not land.
  setState({ handles: next, pending: [...state.pending, handle], error: null });

  const finish = (patch: Partial<WishlistState>) =>
    setState({ ...patch, pending: state.pending.filter((h) => h !== handle) });

  if (!userId) {
    const ok = writeGuestWishlist(next);
    if (!ok) {
      finish({ handles: before, error: "Couldn't save on this device." });
      return { ok: false, reason: "write_failed" };
    }
    finish({});
    track(wasSaved ? "wishlist_remove" : "wishlist_add", { handle });
    return { ok: true, saved: !wasSaved };
  }

  const { error } = wasSaved
    ? await supabase.from("wishlist_items").delete().eq("user_id", userId).eq("product_handle", handle)
    : await supabase
        .from("wishlist_items")
        .upsert({ user_id: userId, product_handle: handle }, { onConflict: "user_id,product_handle", ignoreDuplicates: true });

  if (error) {
    finish({ handles: before, error: wasSaved ? "Couldn't remove that just now." : "Couldn't save that just now." });
    return { ok: false, reason: "write_failed" };
  }
  finish({});
  track(wasSaved ? "wishlist_remove" : "wishlist_add", { handle });
  return { ok: true, saved: !wasSaved };
}
